import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IoClose } from 'react-icons/io5';
import { HiOutlineUserAdd } from 'react-icons/hi';
import { floorplannerStore } from '../../store/floorplannerStore';
import {
  ShareFloorplanDocument,
  UnshareFloorplanDocument,
} from '../../gql/floorplanCollab';

type SharedUser = {
  id: string;
  userName?: string | null;
  email?: string | null;
};

const getInitials = (name?: string | null, email?: string | null) => {
  const source = (name || email || '?').trim();
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
};

type ShareFloorplanModalProps = {
  show: boolean;
  onClose: () => void;
  onMembersChanged?: () => void;
};

/**
 * Enterprise team-share modal for a floorplan. Invites by email; no role picker
 * (everyone shared is an editor).
 */
export const ShareFloorplanModal: React.FC<ShareFloorplanModalProps> = ({
  show,
  onClose,
  onMembersChanged,
}) => {
  const { t } = useTranslation();
  const [inviteEmail, setInviteEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  if (!show) return null;

  const members: SharedUser[] = floorplannerStore.sharedWith || [];
  const owner = floorplannerStore.ownerUser;

  const handleInvite = async () => {
    const email = inviteEmail.trim().toLowerCase();
    if (!email) {
      setError(t('share.pleaseEnterEmail'));
      return;
    }
    if (!floorplannerStore.id || !floorplannerStore.client) return;

    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await floorplannerStore.client.mutate({
        mutation: ShareFloorplanDocument,
        variables: { floorplanId: floorplannerStore.id, email },
      });
      const sharedWith = result.data?.shareFloorplan?.sharedWith ?? [];
      floorplannerStore.setSharedWith(sharedWith);
      setInviteEmail('');
      setSuccess(t('share.inviteSent'));
      onMembersChanged?.();
    } catch (err: any) {
      setError(err?.graphQLErrors?.[0]?.message ?? err?.message ?? t('share.inviteFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (userId: string) => {
    if (!floorplannerStore.id || !floorplannerStore.client) return;
    setRemovingId(userId);
    setError(null);
    try {
      const result = await floorplannerStore.client.mutate({
        mutation: UnshareFloorplanDocument,
        variables: { floorplanId: floorplannerStore.id, userId },
      });
      const sharedWith = result.data?.unshareFloorplan?.sharedWith ?? [];
      floorplannerStore.setSharedWith(sharedWith);
      onMembersChanged?.();
    } catch (err: any) {
      setError(err?.graphQLErrors?.[0]?.message ?? err?.message ?? t('share.removeFailed'));
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="w-full max-w-lg rounded-lg border border-gray-300 bg-[#f7f6f0] shadow-lg">
        <div className="flex items-center justify-between border-b border-[#edebe6] px-4 py-3">
          <div>
            <div className="text-sm font-medium text-black">{t('share.shareWithTeamMember')}</div>
            <div className="text-xs text-gray-600">
              {t('share.inviteCollaborators')}
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-black">
            <IoClose size={22} />
          </button>
        </div>

        <div className="space-y-4 p-4">
          <div className="rounded-lg border border-gray-200 bg-white p-3">
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-gray-500">
              {t('share.addPeopleByEmail')}
            </label>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder={t('share.emailPlaceholder')}
                className="min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-black focus:outline-none"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void handleInvite();
                  }
                }}
              />
              <span className="inline-flex items-center justify-center rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600">
                {t('common.editor')}
              </span>
              <button
                type="button"
                disabled={loading}
                onClick={() => void handleInvite()}
                className="inline-flex items-center justify-center gap-1 rounded-lg bg-boniYellowPrimary px-3 py-2 text-sm font-medium text-black hover:opacity-90 disabled:opacity-50"
              >
                <HiOutlineUserAdd size={16} />
                {t('common.invite')}
              </button>
            </div>
          </div>

          {error && <div className="text-sm text-red-600">{error}</div>}
          {success && <div className="text-sm text-green-700">{success}</div>}

          <div>
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
              {t('share.peopleWithAccess')}
            </h4>
            <ul className="overflow-hidden rounded-lg border border-gray-200 divide-y divide-gray-100 bg-white">
              {owner && (
                <li className="flex items-center justify-between gap-3 px-3 py-3 text-sm">
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-boniYellowPrimary text-xs font-semibold text-black">
                      {getInitials(owner.userName, owner.email)}
                    </span>
                    <div className="min-w-0 flex flex-col">
                      <span className="truncate font-medium text-black">
                        {owner.userName || owner.email || t('common.owner')}
                      </span>
                      {owner.email && (
                        <span className="truncate text-xs text-gray-500">{owner.email}</span>
                      )}
                    </div>
                  </div>
                  <span className="rounded-full bg-boniYellowPrimary px-2.5 py-0.5 text-xs font-medium text-black">
                    {t('common.owner')}
                  </span>
                </li>
              )}
              {members.map((m) => (
                <li key={m.id} className="flex items-center justify-between gap-3 px-3 py-3 text-sm">
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#FBF1B8] text-xs font-semibold text-black">
                      {getInitials(m.userName, m.email)}
                    </span>
                    <div className="min-w-0 flex flex-col">
                      <span className="truncate font-medium text-black">
                        {m.userName || m.email || t('common.collaborator')}
                      </span>
                      {m.email && (
                        <span className="truncate text-xs text-gray-500">{m.email}</span>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    disabled={removingId === m.id}
                    onClick={() => void handleRemove(m.id)}
                    className="text-xs text-red-600 hover:underline disabled:opacity-50"
                  >
                    {t('common.remove')}
                  </button>
                </li>
              ))}
              {members.length === 0 && (
                <li className="px-3 py-3 text-sm text-gray-500">
                  {t('share.noCollaborators')}
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareFloorplanModal;
